import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

import moment from 'moment';
import { BehaviorSubject, finalize, Observable, Subject, Subscription } from 'rxjs';

import { Router } from '@angular/router';
import { App, EvaluatedFieldsProperties, FieldIdentifier, FieldStyleEvaluatedAction, FormulaMapping, Project, SiteFormGridRowItem, SitesLogicServiceModel } from '../models';
import { EvaluatedSiteProperties } from '../models/evaluated-site-properties';
import { Site } from '../models/site';
import { LogicStyledGridRowCellDictionary } from '../models/styled-grid-cell-column-mapping';
import { SNACKBAR_CONSTANTS } from '../shared/constants/snackbar-constants';
import { ClientErrorStatusCodes, ErrorMessages, FieldStyleColor, LicenseTiers, MapPinColor } from '../shared/enums';
import { MapDataService, SiteDataDataService, SitesDataService } from './data-services';
import { DifferencesObject, ReportSiteData } from './logic-services/models';
import { DateConversionService } from './utility-services';
import { ColorsUtilityService } from './utility-services/colors-utility.service';
import { splitNaturalComparer } from './utility-services/natural-sort.service';

@Injectable({
    providedIn: 'root',
})
export class SitesService {
    private _sitesReady: Subject<boolean> = new Subject<boolean>();
    private _siteReady: Subject<boolean> = new Subject<boolean>();
    private _activeSitesCountReady: Subject<boolean> = new Subject<boolean>();
    private _siteSelectionChanged: Subject<Site | null> = new Subject<Site | null>();
    private _siteDataUpdated = new Subject<boolean>();
    private _visibleSitesLoadingSubscription: Subscription;
    private _appSitesLoadingSubscription: Subscription;

    activeSitesCountReady$ = this._activeSitesCountReady.asObservable();
    sitesReady$ = this._sitesReady.asObservable();
    siteReady$ = this._siteReady.asObservable();
    siteDataUpdated$ = this._siteDataUpdated.asObservable();
    siteSelectionChanged$ = this._siteSelectionChanged.asObservable();

    siteSuccessfullyUpdated$: Subject<boolean> = new Subject<boolean>();
    siteSaving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    siteDirty$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    model: SitesLogicServiceModel = new SitesLogicServiceModel();

    dateRegexPattern = /^\d{4}-\d{2}-\d{2}$/;
    dateRegexPatternWithTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;

    constructor(
        private _mapDataService: MapDataService,
        private _snackBar: MatSnackBar,
        private _sitesDataService: SitesDataService,
        private _siteDataDataService: SiteDataDataService,
        private _dateConversionService: DateConversionService,
        private _colorsUtilityService: ColorsUtilityService,
    ) { }

    updateSiteLocally(updatedSite: Site): void {
        this.updateSiteLocallyByList(updatedSite, this.model.sites);
        this.updateSiteLocallyByList(updatedSite, this.model.allSites);

        this.model.sites = this.model.sites.filter(site => site.showSite);

        this.setSite(updatedSite);
        this.setSites(this.model.sites);
        this.setAllSites(this.model.allSites);

        this._sitesReady.next(true);
    }

    updateSiteLocallyByList(updatedSite: Site, sites: Site[]): void {
        const indexToUpdate: number = sites.findIndex(site => site.id == updatedSite.id);
        if (indexToUpdate !== -1) {
            // The API leaves large geoJson (kept in blob storage) off save responses; the cached copy is still current.
            updatedSite.geoJson ??= sites[indexToUpdate].geoJson;
            sites[indexToUpdate] = updatedSite;
        }
        else {
            if (this.model.isNewSiteCreated) {
                sites.unshift(updatedSite);
            }
            else {
                sites.push(updatedSite);
            }
        }
    }

    private restoreGeoJsonFromRequest(savedSite: Site, requestSite: Site): Site {
        savedSite.geoJson ??= requestSite.geoJson;
        return savedSite;
    }

    updateSiteAssociatedProjects(siteId: string, projects: string[]): void {
        const siteToUpdate = this.model.sites.find(site => site.id === siteId);
        if (siteToUpdate) {
            siteToUpdate.projects = projects;
            this.updateSiteLocally(siteToUpdate);
        }
    }

    createSite(app: App, project: Project, site: Site): void {
        if (this.model.isSaving) {
            return;
        }
        this.setIsSaving();
        this._sitesDataService
            .createSite(app, project, site)
            .subscribe({
                next: (savedSite: Site) => {
                    this.model.isNewSiteCreated = true;
                    this.updateSiteLocally(this.restoreGeoJsonFromRequest(savedSite, site));
                    this._snackBar.open(`${app.aliases.site.singular} successfully created`, 'Close', { duration: 5000 });
                    this.resetIsSaving();
                    if (app.activeLicenseTier.id !== LicenseTiers.PerSite) {
                        this.getActiveSitesCountByAppId(app);
                    }
                },
                error: () => {
                    this.resetIsSaving();
                    this.model.isNewSiteCreated = false;
                    this._snackBar.open(`There was an error creating the ${app.aliases.site.singular.toLocaleLowerCase()} record`, 'Close', { duration: 5000 });
                }
            }).add(() => {
                this.model.isNewSiteCreated = false;
            });
    }

    createSiteAndApplyTemplate(app: App, project: Project, updatedSite: Site, selectedSiteTemplateId: string): void {
        if (this.model.isSaving) {
            return;
        }
        this.setIsSaving();
        this._sitesDataService
            .createSiteAndApplyTemplate(app, project, updatedSite, selectedSiteTemplateId)
            .subscribe({
                next: (site: Site) => {
                    this.model.isNewSiteCreated = true;
                    this.updateSiteLocally(this.restoreGeoJsonFromRequest(site, updatedSite));
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS(`${app.aliases.site.singular}`, 'created'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                    this.resetIsSaving();
                    if (app.activeLicenseTier.id !== LicenseTiers.PerSite) {
                        this.getActiveSitesCountByAppId(app);
                    }
                },
                error: () => {
                    this.resetIsSaving();
                    this.model.isNewSiteCreated = false;
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR(`${app.aliases.site.singular}`, 'creating'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }
            }).add(() => {
                this.model.isNewSiteCreated = false;
            });
    }

    updateSite(app: App, site: Site): void {
        this.setIsSaving();
        this._sitesDataService.updateSite(app, site)
            .subscribe({
                next: (savedSite: Site) => {
                    this.updateSiteLocally(this.restoreGeoJsonFromRequest(savedSite, site));
                    this._snackBar.open(`${app.aliases.site.singular} Saved`, 'Close', { duration: 5000 });
                    this.siteSuccessfullyUpdated$.next(true);
                    this.resetIsSaving();
                    this.resetDirtyBit();
                },
                error: () => {
                    this._snackBar.open(`There was an error updating the ${app.aliases.site.singular.toLocaleLowerCase()} record`, 'Close', { duration: 5000 });
                    this.resetIsSaving();
                }
            });
    }

    updateSiteAndApplyTemplate(app: App, project: Project, updatedSite: Site, selectedSiteTemplateId: string): void {
        this.setIsSaving();
        this._sitesDataService.updateSiteAndApplyTemplate(app, project, updatedSite, selectedSiteTemplateId)
            .subscribe({
                next: (site: Site) => {
                    this.updateSiteLocally(this.restoreGeoJsonFromRequest(site, updatedSite));
                    this._snackBar.open(`${app.aliases.site.singular} Updated`, 'Close', { duration: 5000 });
                    this.siteSuccessfullyUpdated$.next(true);
                    this.resetIsSaving();
                    this.resetDirtyBit();
                },
                error: () => {
                    this._snackBar.open(`There was an error updating the ${app.aliases.site.singular.toLocaleLowerCase()} record`, SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                    this.resetIsSaving();
                }
            });
    }

    updateSiteDataAndRunLogics(app: App, project: Project, site: Site): void {
        this.setIsSaving();
        this._siteDataDataService.updateAndRunLogics(app, project, site, site.data)
            .subscribe({
                next: (response: EvaluatedFieldsProperties) => {
                    site.data = response.evaluatedLogicBuilder.siteData;
                    this.updateSiteLocally(site);
                    this._snackBar.open(`${app.aliases.site.singular} Saved`, 'Close', { duration: 5000 });
                    this.siteSuccessfullyUpdated$.next(true);
                    this.resetIsSaving();
                    this.resetDirtyBit();
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR(app.aliases.site.singular.toLocaleLowerCase(), 'updating'), SNACKBAR_CONSTANTS.CLOSE, { duration: 5000 });
                    this.resetIsSaving();
                    this._siteDataUpdated.next(true);
                },
                complete: () => {
                    this._siteDataUpdated.next(true);
                }
            });
    }

    isChargeNumberValid(site: Site): boolean {
        if (!site.chargeNumber || site.chargeNumber === '' || !site.isChargeNumberValid) {
            return false;
        }
        return true;
    }

    shouldDisableActivateSitePerSiteLicense(site: Site): boolean {
        switch (true) {
            case site.active:
                return false;

            case this.isChargeNumberValid(site) && (!site.renewDate):
                return false;

            case (site.renewCountDown || site.renewCountDown === 0) && site.renewCountDown > 0:
                return false;

            case this.isChargeNumberValid(site):
                return false;

            default:
                return true;
        }
    }

    showMessageIfActivateSiteIsDisabled(site: Site, app: App): void {
        if (!this.shouldDisableActivateSitePerSiteLicense(site)) {
            return;
        }

        if (!this.isChargeNumberValid(site)) {
            this._snackBar.open(`To renew this ${app.aliases.site.singular.toLowerCase()} please add a valid charge number by clicking on the edit icon.`, 'Close', { duration: 7000 });
            return;
        }

        this._snackBar.open(`To activate the ${app.aliases.site.singular.toLowerCase()}, please provide a Level 2 ${app.aliases.project.singular} Number.`, 'Close', { duration: 7000 });
    }

    changeShowSite(app: App, site: Site): void {
        site.activeIsLoading = true;
        site.showSiteIsLoading = true;

        this._sitesDataService
            .updateSite(app, { ...site, geoJson: undefined })
            .subscribe({
                next: (site: Site) => {
                    this._snackBar.open(
                        `The ${app.aliases.site.singular.toLowerCase()} '${site.siteName}' is now '${site.showSite ? 'visible' : 'hidden'}' `,
                        'Close',
                        { duration: 5000 }
                    );
                    this.updateSiteLocally(site);
                },
                error: () => {
                    this._snackBar.open(`There was an error updating the ${app.aliases.site.singular.toLowerCase()} record`, 'Close', { duration: 5000 });
                },
                complete: () => { },
            })
            .add(() => {
                site.activeIsLoading = false;
                site.showSiteIsLoading = false;
            });
    }

    deleteSite(currentApp: App, site: Site): void {
        site.isSaving = true;

        this._sitesDataService.deleteSite(currentApp, site)
            .subscribe({
                next: () => {
                    this.model.sites = this.model.sites.filter((existingSite: Site) => existingSite.id !== site.id);
                    this.model.allSites = this.model.allSites.filter((existingSite: Site) => existingSite.id !== site.id);
                    this.setSites(this.model.sites);
                    this.setAllSites(this.model.allSites);
                    this._sitesReady.next(true);
                    this._snackBar.open(`${currentApp.aliases.site.singular} deleted`, 'Close', { duration: 5000 });
                },
                error: () => {
                    this._snackBar.open(`There was an error deleting the ${currentApp.aliases.site.singular.toLowerCase()}`, 'Close', { duration: 5000 });
                },
                complete: () => { },
            })
            .add(() => {
                if (currentApp.activeLicenseTier.id !== LicenseTiers.PerSite) {
                    this.getActiveSitesCountByAppId(currentApp);
                }
                site.isSaving = false;
            });
    }

    setSite(site: Site): void {
        this.model.currentSite = { ...site };
        this._siteReady.next(true);
    }

    clearSite(): void {
        this.model.currentSite = new Site();
        this._siteReady.next(false);
    }

    setSites(sites: Site[]): void {
        sites = sites.filter((site) => site.showSite);
        this.model.isNewSiteCreated ? this.model.sites = [...sites] : this.model.sites = [...sites].sort((a, b) => splitNaturalComparer(a.siteNo, b.siteNo, 'asc'));
    }

    setAllSites(sites: Site[]): void {
        this.model.isNewSiteCreated ? this.model.allSites = [...sites] : this.model.allSites = [...sites].sort((a, b) => splitNaturalComparer(a.siteNo, b.siteNo, 'asc'));
    }

    disconnectSite(): void {
        this.model.currentSite = new Site();
        this._siteReady.next(false);
    }

    disconnectSites(): void {
        this.model.sites = new Array<Site>();
    }

    disconnectAllSites(): void {
        this.model.allSites = new Array<Site>();
    }

    getLatLongByAddress(site: Site): Observable<any> {
        const result$ = new Subject<any>();

        this._sitesDataService.getLatLongByAddress(site)
            .subscribe({
                next: (value) => {
                    result$.next(value);
                    result$.complete();
                },
                complete: () => { },
                error: () => { },
            })
            .add(() => { });

        return result$.asObservable();
    }

    getSitesByAppId(currentApp: App): void {
        if (this.model.sitesLoading && this._appSitesLoadingSubscription) {
            return;
        }

        this.model.sitesLoading = true;

        this._appSitesLoadingSubscription = this._sitesDataService.getSitesByApp(currentApp)
            .subscribe({
                next: (sites: Site[]) => {
                    this.setAllSites(sites);
                    this.setSites(sites);
                    this._sitesReady.next(true);
                    this._snackBar.open(`${currentApp.aliases.site.plural} Loaded`, '', { duration: 2000 });
                },
                error: () => {
                    this.model.allSites = [];
                    this.disconnectAllSites();
                    this._snackBar.open(`Error getting ${currentApp.aliases.site.plural.toLocaleLowerCase()}`, '', { duration: 2000 });
                },
                complete: () => {
                    this._appSitesLoadingSubscription = null;
                },
            });

        this._appSitesLoadingSubscription.add(() => {
            if (currentApp.activeLicenseTier.id !== LicenseTiers.PerSite) {
                this.getActiveSitesCountByAppId(currentApp);
            }
            if (!this._visibleSitesLoadingSubscription) {
                this.model.sitesLoading = false;
            }
        });
    }

    getAdminSites(adminProjects: Project[]): Site[] {
        const adminProjectIds = new Set(adminProjects.map((proj: Project) => proj.id));
        return this.model.sites.filter((site: Site) =>
            site.projects?.some((projId: string) => adminProjectIds.has(projId))
        );
    }

    getVisibleSites(app: App): void {
        if (!this.model.sitesLoading && this.model.sites.length === 0) {
            this.model.sitesLoading = true;
            this.model.currentAppId = app.id;

            this._visibleSitesLoadingSubscription = this._sitesDataService.getVisibleSites(app)
                .subscribe({
                    next: (sites: Site[]) => {
                        this.setSites(sites);
                        this._sitesReady.next(true);
                    },
                    error: () => {
                        this.model.sites = new Array<Site>();
                        this._snackBar.open(`Error getting ${app.aliases.site.plural.toLowerCase()}`, '', { duration: 2000 });
                        this.disconnectSites();
                    },
                    complete: () => {
                        this._visibleSitesLoadingSubscription = null;
                    },
                });

            this._visibleSitesLoadingSubscription.add(() => {
                this.model.sitesLoading = false;
            });
        }
        else {
            setTimeout(() => {
                this.setSites(this.model.sites);
                this._sitesReady.next(true);
            }, 100);
        }
    }

    getSingleSiteBySiteId(app: App, siteId: string): void {
        this.model.siteLoading = true;

        this._sitesDataService.getSingleSite(app, siteId)
            .subscribe({
                next: (site: Site) => {
                    this.updateSiteLocally(site);
                },
                error: () => { },
                complete: () => { },
            })
            .add(() => {
                this.model.siteLoading = false;
            });
    }

    getSingleSite(app: App, site: Site): void {
        if (site.id === '' || site === undefined) {
            return;
        }

        if (site.dateCreated) {
            this.updateSiteLocally(site);
            return;
        }

        this.model.siteLoading = true;

        this._sitesDataService.getSingleSite(app, site.id)
            .subscribe({
                next: (site: Site) => {
                    if (site.data) {
                        this.mapDatesToDateObjectInGrids(site);
                    }
                    this.updateSiteLocally(site);
                },
                error: () => { },
                complete: () => { },
            })
            .add(() => {
                this.model.siteLoading = false;
            });
    }

    validateSite(app: App, site: Site): void {
        const siteFound = this.model.allSites.find(siteFound => siteFound.id === site.id);

        if (!siteFound) {
            this.getSingleSiteForMapSiteDataPreview(app, site);
            return;
        }

        if (siteFound?.data) {
            this.setSite(siteFound);
        }
        else {
            this.getSingleSiteForMapSiteDataPreview(app, site);
        }
    }

    getSingleSiteForMapSiteDataPreview(app: App, incomingSite: Site): void {
        this._sitesDataService.getSingleSite(app, incomingSite.id)
            .subscribe({
                next: (site: Site) => {
                    if (site.data) {
                        this.mapDatesToDateObjectInGrids(site);
                    }

                    this.updateSiteLocallyByList(site, this.model.sites);
                    this.updateSiteLocallyByList(site, this.model.allSites);
                    this.setSite(site);
                },
                error: () => { },
                complete: () => { },
            });
    }

    siteSelectionChange(app: App, selectedSite: Site | null): void {
        if (!selectedSite) {
            this.disconnectSite();
            this._siteSelectionChanged.next(null);
            return;
        }
        this.getSingleSite(app, selectedSite);
        this._siteSelectionChanged.next(selectedSite);
    }

    siteDataPreviewSiteSelectionChange(app: App, selectedSite: Site | null): void {
        if (!selectedSite) {
            this.disconnectSite();
            return;
        }
        this.validateSite(app, selectedSite);
    }

    mapDatesToDateObjectInGrids(site: Site): void {
        for (const dataItem in site.data) {
            if (Array.isArray(site.data[dataItem])) {
                site.data[dataItem].forEach((row: SiteFormGridRowItem) => {
                    if (row.id) {
                        this.mapDateObject(row);
                    }
                });
            }
        }
    }

    mapDateObject(row: SiteFormGridRowItem): void {
        for (const key in row) {
            if (row[key] && this.dateRegexPatternWithTime.test(row[key]) || this.dateRegexPattern.test(row[key])) {
                row[key] = this._dateConversionService.convertToDate(row[key]);
            }
        }
    }

    formatGridDatesToString(data): void {
        for (const dataItem in data) {
            if (Array.isArray(data[dataItem])) {
                data[dataItem].forEach((row: SiteFormGridRowItem) => {
                    if (row.id) {
                        this.mapDateToString(row);
                    }
                });
            }
        }
    }

    mapDateToString(row: SiteFormGridRowItem): void {
        for (const key in row) {
            if (moment.isMoment(row[key]) || row[key] instanceof Date) {
                row[key] = this._dateConversionService.convertDateToString(row[key]);
            }
        }
    }

    getActiveSitesCountByAppId(currentApp: App): void {
        this._sitesDataService.getSitesCountByAppId(currentApp.id).subscribe({
            next: (count: number) => {
                currentApp.activeSitesCount = count;
                this._activeSitesCountReady.next(true);
            },
            error: () => { },
            complete: () => { },
        });
    }

    toggleActive(currentApp: App, site: Site, newActiveStatus: boolean, isAppLicensePerSite: boolean): void {
        site.activeIsLoading = true;

        this._sitesDataService
            .updateSiteActive(currentApp.id, site, newActiveStatus)
            .subscribe({
                next: (site: Site) => {
                    if (this.isRenewalDateYearFromNow(site) && site.active) {
                        this._snackBar.open(`${currentApp.aliases.site.singular} expiration date has been set and ${site.siteName}' is now active`, 'Close', { duration: 5000 });
                    }
                    else {
                        this._snackBar.open(
                            `The ${currentApp.aliases.site.singular} '${site.siteName}' is now '${site.active ? 'active' : 'de-activated'}' `,
                            'Close',
                            { duration: 5000 }
                        );
                    }
                    if (!isAppLicensePerSite) {
                        this.getActiveSitesCountByAppId(currentApp);
                    }
                    this.updateSiteLocally(site);
                },
                error: (error) => {
                    if (error?.status === ClientErrorStatusCodes.Conflict) {
                        switch (error.error) {
                            case ErrorMessages.pastSiteMaxLimit:
                                this._snackBar.open(`Action disabled. You are over your active ${currentApp.aliases.site.singular} limit.`, 'Close', { duration: 5000 });
                                break;
                            case ErrorMessages.invalidChargeNumber:
                                this._snackBar.open(ErrorMessages.invalidChargeNumber, 'Close', { duration: 5000 });
                                break;
                            default:
                                this._snackBar.open('There was an error updating the record', 'Close', { duration: 5000 });
                        }
                    }

                    site.active = !newActiveStatus;
                },
                complete: () => { },
            })
            .add(() => {
                site.activeIsLoading = false;
            });
    }

    activateAllSites(currentApp: App, sitesAliasLowerCase: string): void {
        this.setIsSaving();
        this._sitesDataService.activateAllSites(currentApp).subscribe({
            next: () => {
                this.resetIsSaving();
                this.getSitesByAppId(currentApp);
                this._snackBar.open(`All the ${sitesAliasLowerCase} have been activated`, "Close", { duration: 5000 });
                if (currentApp.activeLicenseTier.id !== LicenseTiers.PerSite) {
                    this.getActiveSitesCountByAppId(currentApp);
                }
            },
            error: () => {
                this.resetIsSaving();
                this._snackBar.open(`Error activating all ${sitesAliasLowerCase}`);

            },
            complete: () => { },
        })
            .add(() => {
            });
    }

    getProjectSiteEvaluatedLogicBuilderFormulaFields(app: App, project: Project, site: Site): void {
        const evaluatedFieldsPropertiesRequest$ = project.isSummaryProject === true
            ? this._siteDataDataService.getSummaryProjectSiteEvaluatedFieldsProperties(app, project, site)
            : this._siteDataDataService.getSingleSiteEvaluatedFieldsProperties(app, project, site);

        evaluatedFieldsPropertiesRequest$
            .pipe(
                finalize(() => {
                    this._siteDataUpdated.next(true);
                })
            )
            .subscribe({
                next: (response: EvaluatedFieldsProperties) => {
                    if (!response.evaluatedLogicBuilder.triggerFields.length) {
                        if (!this.model.siteEvaluatedProperties.has(site.id)) {
                            this.model.siteEvaluatedProperties.set(site.id, new EvaluatedSiteProperties());
                        }
                        this.model.siteEvaluatedProperties.get(site.id).fetchedEvaluatedLogicBuilderFormulaFieldsAssociatedToSite.set(project.id, false);
                    }

                    this.updateEvaluatedProperties(site, project, response);
                    this.updateSiteLocally(site);
                },
                error: () => { },
            });
    }

    getProjectSiteEvaluatedMapPinColor(app: App, project: Project, site: Site): void {
        if (!project?.id) {
            return;
        }
        this._mapDataService.getEvaluatedMapPinColorsBySiteId(app, project, site.id)
            .pipe(
                finalize(() => {
                    this._siteDataUpdated.next(true);
                })
            )
            .subscribe({
                next: (response: Record<string, MapPinColor>) => {
                    if (!response.hasOwnProperty(site.id)) {
                        if (!this.model.siteEvaluatedProperties.has(site.id)) {
                            this.model.siteEvaluatedProperties.set(site.id, new EvaluatedSiteProperties());
                        }
                        this.model.siteEvaluatedProperties.get(site.id)?.fetchedEvaluatedLogicBuilderFormulaFieldsAssociatedToSite.set(project.id, false);
                    }

                    this.updateSiteMapPinEvaluatedProperties(site, project, response);
                    this.updateSiteLocally(site);
                },
            });
    }

    getProjectSiteEvaluatedFormulaFields(app: App, project: Project, site: Site): void {
        const evaluatedFormulaFieldsRequest$ = project.isSummaryProject === true
            ? this._siteDataDataService.getSummaryProjectSiteEvaluatedFormulaFields(app, project, site)
            : this._siteDataDataService.getSingleSiteEvaluatedFormulaFields(app, project, site);

        evaluatedFormulaFieldsRequest$
            .pipe(
                finalize(() => {
                    this._siteDataUpdated.next(true);
                })
            )
            .subscribe({
                next: (response: EvaluatedFieldsProperties) => {
                    this.updateEvaluatedFormulaProperties(site, project, response);
                    this.updateSiteLocally(site);
                },
                error: () => { },
            });
    }

    patchField(app: App, project: Project, site: Site, updatedField: DifferencesObject): void {
        this.setIsSaving();
        this._siteDataDataService.patchField(app, project, site, updatedField)
            .subscribe({
                next: (site: Site) => {
                    this._snackBar.dismiss();
                    this.mapDatesToDateObjectInGrids(site);
                    this.updateSiteLocally(site);
                    this._snackBar.open(`${app.aliases.site.singular} data saved`, 'Close', { duration: 2000 });
                    this.resetDirtyBit();
                    this.resetIsSaving();
                },
                error: () => {
                    this._snackBar.open('An error has occurred while saving', 'Close', { duration: 2000 });
                    this.resetIsSaving();
                    this._siteDataUpdated.next(true);
                },
                complete: () => {
                    this._siteDataUpdated.next(true);
                }
            });
    }

    patchLogicBuilderTriggerRepositionGrid(app: App, project: Project, site: Site, updatedField: DifferencesObject): void {
        this.setIsSaving();
        this._siteDataDataService.patchLogicBuilderTriggerRepositionGrid(app, project, site, updatedField)
            .subscribe({
                next: (response: EvaluatedFieldsProperties) => {
                    this.updateEvaluatedProperties(site, project, response);
                    this._snackBar.dismiss();
                    this.mapDatesToDateObjectInGrids(site);
                    this.updateSiteLocally(site);
                    this._snackBar.open(`${app.aliases.site.singular} data saved`, 'Close', { duration: 2000 });
                    this.resetIsSaving();
                },
                error: () => {
                    this._snackBar.open('An error has occurred while saving', 'Close', { duration: 2000 });
                    this.resetIsSaving();
                    this._siteDataUpdated.next(true);
                },
                complete: () => {
                    this._siteDataUpdated.next(true);
                }
            });
    }

    patchLogicBuilderTriggerField(app: App, project: Project, site: Site, updatedField: DifferencesObject): void {
        this.setIsSaving();
        this._siteDataDataService.patchLogicBuilderTriggerField(app, project, site, updatedField)
            .subscribe({
                next: (response: EvaluatedFieldsProperties) => {
                    this.updateEvaluatedProperties(site, project, response);
                    this._snackBar.dismiss();

                    this.mapDatesToDateObjectInGrids(site);
                    this.updateSiteLocally(site);
                    this._snackBar.open(`${app.aliases.site.singular} data saved`, 'Close', { duration: 2000 });
                    this.resetDirtyBit();
                    this.resetIsSaving();
                },
                error: () => {
                    this._snackBar.open('An error has occurred while saving', 'Close', { duration: 2000 });
                    this.resetIsSaving();
                    this._siteDataUpdated.next(true);
                },
                complete: () => {
                    this._siteDataUpdated.next(true);
                }
            });
    }

    patchFormulaOperandField(app: App, project: Project, site: Site, updatedField: DifferencesObject): void {
        this.setIsSaving();
        this._siteDataDataService.patchFormulaOperandField(app, project, site, updatedField)
            .subscribe({
                next: (site: Site) => {
                    this._snackBar.dismiss();
                    this.mapDatesToDateObjectInGrids(site);
                    this.updateSiteLocally(site);
                    this._snackBar.open(`${app.aliases.site.singular} data saved`, 'Close', { duration: 2000 });
                    this.resetDirtyBit();
                    this.resetIsSaving();
                },
                error: () => {
                    this._snackBar.open('An error has occurred while saving', 'Close', { duration: 2000 });
                    this.resetIsSaving();
                    this._siteDataUpdated.next(true);
                },
                complete: () => {
                    this._siteDataUpdated.next(true);
                }
            });
    }

    removeField(app: App, project: Project, site: Site, updatedField: DifferencesObject): void {
        this._siteDataDataService.removeField(app, project, site, updatedField)
            .subscribe({
                next: (site: Site) => {
                    this._snackBar.dismiss();

                    this.mapDatesToDateObjectInGrids(site);
                    this.updateSiteLocally(site);
                    this._snackBar.open(`${app.aliases.site.singular} data saved`, 'Close', { duration: 2000 });
                },
                error: () => {
                    this._snackBar.open('An error has occurred while saving', 'Close', { duration: 2000 });

                    this._siteDataUpdated.next(true);
                },
                complete: () => {
                    this._siteDataUpdated.next(true);
                }
            });
    }

    removeLogicBuilderTriggerField(app: App, project: Project, site: Site, updatedField: DifferencesObject): void {
        this._siteDataDataService.removeLogicBuilderTriggerField(app, project, site, updatedField)
            .subscribe({
                next: (response: EvaluatedFieldsProperties) => {
                    this.updateEvaluatedProperties(site, project, response);
                    this._snackBar.dismiss();

                    this.mapDatesToDateObjectInGrids(site);
                    this.updateSiteLocally(site);
                    this._snackBar.open(`${app.aliases.site.singular} data saved`, 'Close', { duration: 2000 });
                },
                error: () => {
                    this._snackBar.open('An error has occurred while saving', 'Close', { duration: 2000 });

                    this._siteDataUpdated.next(true);
                },
                complete: () => {
                    this._siteDataUpdated.next(true);
                }
            });
    }

    removeFormulaOperandField(app: App, project: Project, site: Site, updatedField: DifferencesObject): void {
        this._siteDataDataService.removeFormulaOperandField(app, project, site, updatedField)
            .subscribe({
                next: (site: Site) => {
                    this._snackBar.dismiss();
                    this.mapDatesToDateObjectInGrids(site);
                    this.updateSiteLocally(site);
                    this._snackBar.open(`${app.aliases.site.singular} data saved`, 'Close', { duration: 2000 });
                    this.resetDirtyBit();
                    this.resetIsSaving();
                },
                error: () => {
                    this._snackBar.open('An error has occurred while saving', 'Close', { duration: 2000 });
                    this.resetIsSaving();
                    this._siteDataUpdated.next(true);
                },
                complete: () => {
                    this._siteDataUpdated.next(true);
                }
            });
    }

    getFieldStyleClass(site: Site, fieldId: string): string {
        let color: FieldStyleColor;
        const evaluatedProperties = this.model.siteEvaluatedProperties.get(site.id);
        if (evaluatedProperties === undefined) {
            return '';
        }
        evaluatedProperties.fieldStyleEvaluatedActions?.find((action: FieldStyleEvaluatedAction) => {
            if (action.fieldId === fieldId) {
                color = action.colorId;
            }
        });

        return color ? this._colorsUtilityService.getFieldStyleColorClassByColor(color) : '';
    }

    isFieldASettableField(currentSite: Site, fieldId: string): boolean {
        const evaluatedProperties = this.model.siteEvaluatedProperties.get(currentSite.id);
        if (evaluatedProperties === undefined) {
            return false;
        }
        return evaluatedProperties.fieldsSettableByLogicBuilders?.some((field: FieldIdentifier) => field.fieldId === fieldId);
    }

    getGridCellStyleClass(site: Site, gridId: string, rows: SiteFormGridRowItem[], logicCellStyleMapping: Map<string, LogicStyledGridRowCellDictionary>): void {
        if (!rows || rows?.length === 0) {
            return;
        }
        logicCellStyleMapping.clear();
        const evaluatedProperties = this.model.siteEvaluatedProperties.get(site.id);
        if (!evaluatedProperties?.fieldStyleEvaluatedActions) {
            return;
        }

        const actionsForGrid = evaluatedProperties.fieldStyleEvaluatedActions?.filter(
            (action: FieldStyleEvaluatedAction) => (action.fieldId === gridId && action.gridColumnId));

        if (actionsForGrid) {
            const rowsActionsApply = rows?.filter(row => actionsForGrid.some(action => action.gridRowId === row.id));
            rowsActionsApply.forEach((row: SiteFormGridRowItem) => {
                const styledGridColForThisRow = new LogicStyledGridRowCellDictionary();
                actionsForGrid.forEach((action: FieldStyleEvaluatedAction) => {
                    if (row.id === action.gridRowId) {
                        styledGridColForThisRow[action.gridColumnId] = this._colorsUtilityService.getFieldStyleColorClassByColor(action.colorId);
                    }
                });
                logicCellStyleMapping.set(row.id, styledGridColForThisRow);
            });
        }
    }

    getRowStyleClass(site: Site, project: Project, gridId: string, rows: SiteFormGridRowItem[], logicCellStyleMapping: Map<string, LogicStyledGridRowCellDictionary>): void {
        const evaluatedProperties = this.model.siteEvaluatedProperties.get(site.id);
        const actionsForGrid = evaluatedProperties.fieldStyleEvaluatedActions?.filter(
            (action: FieldStyleEvaluatedAction) => (action.fieldId === gridId && !action.gridColumnId));

        if (actionsForGrid) {
            actionsForGrid.forEach((action: FieldStyleEvaluatedAction) => {
                const row = rows.find((row: SiteFormGridRowItem) => row.id === action.gridRowId);
                if (row) {
                    if (logicCellStyleMapping.has(row.id)) {
                        logicCellStyleMapping.get(row.id).rowStyleClass = this._colorsUtilityService.getFieldStyleColorClassByColor(action.colorId);
                    }
                    else {
                        const dictionary = new LogicStyledGridRowCellDictionary();
                        dictionary.rowStyleClass = this._colorsUtilityService.getFieldStyleColorClassByColor(action.colorId);
                        logicCellStyleMapping.set(row.id, dictionary);
                    }
                }
            });
        }
    }

    isFieldATriggerField(siteToUpdated: Site, fieldToPatch: DifferencesObject): boolean {
        if (!this.model.siteEvaluatedProperties.has(siteToUpdated.id)) {
            return false;
        }

        const triggerFields = this.model.siteEvaluatedProperties.get(siteToUpdated.id).triggerFields;
        if (triggerFields?.some((triggerField: FieldIdentifier) => triggerField.fieldId === fieldToPatch.fieldId || (triggerField.gridColumnId && fieldToPatch.objectFieldId) && triggerField.gridColumnId === fieldToPatch.objectFieldId)) {
            return true;
        }

        return false;
    }

    isFieldAFormulaOperandField(siteToUpdated: Site, fieldToPatch: DifferencesObject) {
        if (!this.model.siteEvaluatedProperties.has(siteToUpdated.id)) {
            return false;
        }

        const operandFields = this.model.siteEvaluatedProperties.get(siteToUpdated.id).formulaOperands;
        if (operandFields?.some((operandField: FieldIdentifier) => operandField.fieldId === fieldToPatch.fieldId || (operandField.gridColumnId && fieldToPatch.objectFieldId) && operandField.gridColumnId === fieldToPatch.objectFieldId)) {
            return true;
        }

        return false;
    }

    isFormulaForOperandATriggerField(site: Site, formulaOperandField: DifferencesObject): boolean {
        const triggerFields = this.model.siteEvaluatedProperties.get(site.id).triggerFields;
        const formulaMappings = this.model.siteEvaluatedProperties.get(site.id).formulaMappings;
        const formulasWithOperand = formulaMappings.filter((mapping: FormulaMapping) =>
            mapping.operands.some((operand: FieldIdentifier) =>
                operand.fieldId === formulaOperandField.fieldId && operand.gridColumnId === formulaOperandField.objectFieldId));
        if (formulasWithOperand.length === 0) {
            return false;
        }

        return formulasWithOperand.some((mapping: FormulaMapping) =>
            triggerFields.some((triggerField: FieldIdentifier) =>
                triggerField.fieldId === mapping.formula.fieldId &&
                triggerField.gridColumnId === mapping.formula.gridColumnId));
    }

    updateEvaluatedProperties(site: Site, project: Project, response: EvaluatedFieldsProperties): void {
        site.data = response.evaluatedLogicBuilder.siteData;
        if (!this.model.siteEvaluatedProperties.has(site.id)) {
            this.model.siteEvaluatedProperties.set(site.id, new EvaluatedSiteProperties());
        }

        const evaluatedProperties = this.model.siteEvaluatedProperties.get(site.id);
        evaluatedProperties.fieldStyleEvaluatedActions = response.evaluatedLogicBuilder.fieldStyleEvaluatedActions;
        evaluatedProperties.fieldsSettableByLogicBuilders = response.evaluatedLogicBuilder.fieldsSettableByLogicBuilders;
        evaluatedProperties.triggerFields = response.evaluatedLogicBuilder.triggerFields;
        evaluatedProperties.mapPinColorsByProject.set(project.id, response.evaluatedLogicBuilder.mapPinColor);
        evaluatedProperties.fetchedEvaluatedLogicBuilderFormulaFieldsAssociatedToSite.set(project.id, true);
        evaluatedProperties.formulaOperands = response.formulaProperties.formulaOperands;
        evaluatedProperties.formulaMappings = response.formulaProperties.formulaMappings;
    }

    updateSiteMapPinEvaluatedProperties(site: Site, project: Project, response: Record<string, MapPinColor>): void {
        if (!this.model.siteEvaluatedProperties.has(site.id)) {
            this.model.siteEvaluatedProperties.set(site.id, new EvaluatedSiteProperties());
        }

        const evaluatedProperties = this.model.siteEvaluatedProperties.get(site.id);
        if (!evaluatedProperties || !project?.id) {
            return;
        }
        evaluatedProperties.mapPinColorsByProject.set(project.id, response[site.id]);
        evaluatedProperties.fetchedEvaluatedLogicBuilderFormulaFieldsAssociatedToSite.set(project.id, true);
    }

    updateEvaluatedFormulaProperties(site: Site, project: Project, response: EvaluatedFieldsProperties): void {
        site.data = response.evaluatedLogicBuilder.siteData;
        if (!this.model.siteEvaluatedProperties.has(site.id)) {
            this.model.siteEvaluatedProperties.set(site.id, new EvaluatedSiteProperties());
        }

        const evaluatedProperties = this.model.siteEvaluatedProperties.get(site.id);
        evaluatedProperties.fetchedEvaluatedLogicBuilderFormulaFieldsAssociatedToSite.set(project.id, true);
        evaluatedProperties.formulaOperands = response.formulaProperties.formulaOperands;
        evaluatedProperties.formulaMappings = response.formulaProperties.formulaMappings;
    }

    updateSetFieldsInGrid(currentSite: Site, gridId: string): FieldIdentifier[] {
        const evaluatedProperties = this.model.siteEvaluatedProperties.get(currentSite.id);
        if (evaluatedProperties === undefined) {
            return [];
        }

        return evaluatedProperties.fieldsSettableByLogicBuilders?.filter((field: FieldIdentifier) => field.fieldId === gridId);
    }

    getDummySite(): Site {
        const dummySite = new Site();
        dummySite.id = 'dummy-site-id';
        dummySite.siteName = 'Site Name';
        dummySite.siteNo = '{Site number}';
        dummySite.projects = ['dummy-project-id'];
        dummySite.longitude = 0;
        dummySite.latitude = 0;
        dummySite.data = {
            city: 'Dummy City',
            state: 'Dummy State',
        };

        return dummySite;
    }

    isRenewalDateYearFromNow(site: Site): boolean {
        const today = new Date();
        const siteRenewDate = new Date(site.renewDate);
        const newExpirationDate = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
        const siteRenewDateNormalized = new Date(siteRenewDate.getFullYear(), siteRenewDate.getMonth(), siteRenewDate.getDate());

        return siteRenewDateNormalized === newExpirationDate;
    }

    setIsSaving(): void {
        this.model.isSaving = true;
        this.siteSaving$.next(true);
    }

    resetIsSaving(): void {
        this.model.isSaving = false;
        this.siteSaving$.next(false);
    }

    setDirtyBit(): void {
        this.model.isDirty = true;
        this.siteDirty$.next(true);
    }

    resetDirtyBit(): void {
        this.model.isDirty = false;
        this.siteDirty$.next(false);
    }

    navigateToSelectedSite(event: MouseEvent, siteData: ReportSiteData, currentApp: App, router: Router): void {
        event.stopPropagation();
        const siteId: string = siteData.SiteId.toString();
        this.getSingleSiteBySiteId(currentApp, siteId);
        router.navigate([`/app/${currentApp.url}/site/${siteId}`]);
    }
}
